import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Download, ShieldCheck, FileText, Info, BookOpen, Clock } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import { ARTICLES } from "../../data/articles";

const SECTIONS = [
  {
    heading: "For HR & People Leaders",
    intro: "Practical, executive-friendly material to help HR leaders shape a workforce care strategy that goes beyond a hotline.",
    items: [
      { kind: "Field Guide · PDF", title: "Building a Culture of Care: A Practical Field Guide", desc: "How to design daily, relational workforce care into the fabric of how your organization already operates.", href: "/resource-files/culture-of-care-field-guide.pdf" },
      { kind: "Field Guide · PDF", title: "Workforce Care That Goes Beyond a Hotline", desc: "Five practical moves for HR leaders standing up relational chaplaincy alongside an existing EAP — without disrupting what already works.", href: "/resource-files/hr-people-leaders.pdf" },
      { kind: "Checklist · PDF", title: "Crisis Response Planning Checklist", desc: "What to have in place before a workplace incident, sudden loss, or layoff — so the response feels human, not transactional.", href: "/resource-files/crisis-response-planning-checklist.pdf" },
      { kind: "Brief", title: "Chaplaincy vs. EAP: A 1-Page Comparison Brief", desc: "A printable side-by-side overview HR leaders can share with CHROs, CEOs, and boards.", to: "/resources/chaplaincy-vs-eap", internal: true },
    ],
  },
  {
    heading: "For Executives & Founders",
    intro: "Quiet, candid material for the people carrying the weight at the top of the organization.",
    items: [
      { kind: "Article · PDF", title: "Leadership Fatigue: Recognizing the Quiet Signs", desc: "Common signs that a leader may be carrying sustained pressure — and practical ways colleagues and organizations can respond with care.", href: "/resource-files/leadership-fatigue-quiet-signs.pdf" },
      { kind: "Brief · PDF", title: "The Hidden ROI of Workforce Care", desc: "A practical look at the organizational value of relational workforce care — including utilization, access, workforce support, and ways leaders can evaluate whether their care strategy is reaching people.", href: "/resource-files/hidden-roi-workforce-care.pdf" },
      { kind: "Overview · PDF", title: "Executive Confidential Support — How It Works", desc: "A short overview of what a private executive chaplain engagement actually looks like, day to day.", href: "/resource-files/executive-confidential-support.pdf" },
      { kind: "Executive Brief · PDF", title: "Quiet Leadership Support, On Your Terms", desc: "What a private executive chaplain engagement actually looks like — confidential, off-the-org-chart, and built around your real calendar.", href: "/resource-files/executives-founders.pdf" },
    ],
  },
  {
    heading: "For Employees",
    intro: "Calm, low-stakes resources employees can read first — before, during, or instead of a chaplain conversation.",
    items: [
      { kind: "Article · PDF", title: "What to Expect From a Chaplain Conversation", desc: "A plain-language walkthrough so employees know what's offered, what's optional, and what stays private.", href: "/resource-files/what-to-expect-chaplain-conversation.pdf" },
      { kind: "Toolkit · PDF", title: "Stress, Sleep, and Workday Resilience", desc: "Small, practical practices for the moments when work doesn't slow down.", href: "/resource-files/stress-sleep-workday-resilience.pdf" },
      { kind: "Guide · PDF", title: "Grief at Work: A Compassionate Workplace Guide", desc: "How to be present with grieving colleagues — for managers, peers, and the people themselves.", href: "/resource-files/grief-at-work-workplace-guide.pdf" },
      { kind: "Employee Guide · PDF", title: "A Quiet Conversation, On Your Terms", desc: "What to expect from a chaplain conversation — what's offered, what's optional, and what stays private. Plain language, no jargon.", href: "/resource-files/employee-guide.pdf" },
    ],
  },
  {
    heading: "Workplace Stress & Burnout",
    intro: "Recognizing the quieter signs of workplace strain that can be easy to overlook.",
    items: [
      { kind: "Brief · PDF", title: "Burnout Isn't a Productivity Problem", desc: "Five signs of sustained workplace strain worth noticing — and practical ways leaders can respond with greater awareness and support.", href: "/resource-files/workplace-stress-burnout.pdf" },
    ],
  },
  {
    heading: "Grief & Loss Support",
    intro: "Compassionate, executive-friendly language for the days, weeks, and months that follow a loss.",
    items: [
      { kind: "Workplace Guide · PDF", title: "Grief Doesn't Stay Home When People Come to Work", desc: "Practical, human guidance for managers, peers, and grieving employees — including why support may still matter weeks and months after a loss.", href: "/resource-files/grief-loss-support.pdf" },
    ],
  },
  {
    heading: "Crisis & Critical Incident Support",
    intro: "What to have ready before you need it — so the response feels human, not transactional.",
    items: [
      { kind: "Checklist · PDF", title: "The First 24 Hours Set the Tone for Months", desc: "Seven practical considerations for the initial response after a critical incident, along with guidance for thoughtful follow-up in the weeks and months that follow.", href: "/resource-files/crisis-critical-incident.pdf" },
    ],
  },
];

export default function PublicResources() {
  usePageMeta({
    title: "Resources | California Corporate Chaplains",
    description: "Practical workforce wellness material — briefs, guides, and toolkits for HR leaders, executives, founders, and the employees they support.",
  });
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="public-resources">
      <div className="max-w-3xl mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Resources</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]">Practical Workforce Care Resources for Today's Organizations</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">Briefs, guides, and toolkits curated for HR leaders, executives, founders, and the employees they support.</p>
      </div>

      <aside className="mb-12 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 flex items-start gap-3 text-sm" data-testid="care-portal-notice">
        <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
        <p className="text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">Looking for Care Portal resources?</span> Employees of enrolled organizations can access confidential Care Portal resources at{" "}
          <Link to="/care-portal/resources" className="text-[#4A6FA5] hover:text-[#3B597B] font-medium underline underline-offset-2">/care-portal/resources</Link>.
        </p>
      </aside>

      {/* Two-track resource experience: Articles & Insights (read) + Guides & Downloads (download) */}
      <section className="mb-14" data-testid="resources-articles-teaser">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Articles &amp; Insights</p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">Read Practical Perspectives Online</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">Practical perspectives on workplace care, leadership, grief, employee well-being, and organizational chaplaincy — written for the people leading and supporting today's workforce.</p>
          </div>
          <Link
            to="/resources/articles"
            data-testid="resources-view-all-articles"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 hover:border-[#4A6FA5] hover:text-[#4A6FA5] text-sm font-medium whitespace-nowrap self-start"
          >
            View All Articles &amp; Insights <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ARTICLES.slice(0, 4).map((a) => (
            <Link
              key={a.slug}
              to={`/resources/articles/${a.slug}`}
              data-testid={`resources-article-teaser-${a.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#4A6FA5] hover:shadow-md transition-all flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-3.5 h-3.5 text-[#4A6FA5]" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">{a.category}</span>
              </div>
              <p className="font-heading text-base text-slate-900 leading-snug">{a.title}</p>
              <div className="mt-auto pt-4 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-slate-500"><Clock className="w-3.5 h-3.5" /> {a.readMins} min</span>
                <span className="text-[#4A6FA5] font-medium group-hover:underline">Read Article</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Guides & Downloads intro */}
      <div className="mb-8 max-w-3xl" data-testid="resources-guides-intro">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Guides &amp; Downloads</p>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">Practical Briefs, Field Guides, and Toolkits</h2>
        <p className="mt-3 text-slate-600 leading-relaxed">Practical briefs, field guides, checklists, and toolkits you can download, save, and share with your organization.</p>
      </div>

      <div className="space-y-16">
        {SECTIONS.map((section) => (
          <section key={section.heading} data-testid={`resources-section-${section.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">Section</p>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">{section.heading}</h2>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-3xl">{section.intro}</p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {section.items.map((item) => (
                <li key={item.title}>
                  {item.internal ? (
                    <Link to={item.to} data-testid={`resource-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block h-full bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]">
                      <ResourceCardInner item={item} internal />
                    </Link>
                  ) : (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={`resource-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block h-full bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]">
                      <ResourceCardInner item={item} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <section className="mt-20 rounded-2xl bg-[#4A6FA5] text-white p-8 md:p-12 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">A quiet first conversation about workplace support.</h2>
        <p className="mt-3 text-blue-100 max-w-2xl mx-auto">Tell us a little about your team. We'll follow up confidentially within one business day — exploratory, never a pitch.</p>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/schedule" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white text-[#4A6FA5] font-medium hover:bg-blue-50">Request a Consultation <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/services" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-blue-200 text-white font-medium hover:bg-[#3B597B]">Explore services</Link>
        </div>
      </section>
    </div>
  );
}

function ResourceCardInner({ item, internal }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]"><FileText className="w-3.5 h-3.5" /> {item.kind}</span>
        {internal ? <Info className="w-4 h-4 text-slate-400 group-hover:text-[#4A6FA5]" /> : <Download className="w-4 h-4 text-slate-400 group-hover:text-[#4A6FA5]" />}
      </div>
      <h3 className="mt-4 font-heading text-lg font-medium text-slate-900 leading-snug">{item.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold group-hover:gap-2 transition-all">{internal ? "View Resource" : "Download PDF"} <ArrowRight className="w-3.5 h-3.5" /></span>
    </>
  );
}
